import React, { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import icons from "../../../assets/images/constantIcons";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import toast from "react-hot-toast";
import { UserContext } from "../../Layout/UserContext";

const PayNowPlayer = ({
  isOpen,
  onClose,
  dueamount,
  fetchCustomers,
  customerId,
  customerName,
}) => {
  const [showDiscount, setShowDiscount] = useState(false);
  const [isToastShown, setIsToastShown] = useState(false);
  const [showExtraAmount, setShowExtraAmount] = useState(false);
  const { userData } = useContext(UserContext);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      totalAmount: dueamount,
      amount_to_be_pay: dueamount,
      payment_type: "",
      discount: "",
      balanceDue: "",
      remark: "",
      extra_amount: "",
    },
    validationSchema: Yup.object({
      amount_to_be_pay: Yup.number()
        // .min(1, "Amount must be at least ₹1")
        .required("Amount to be Paid is required"),
      payment_type: Yup.string().required("Payment mode is required"),
      ...(dueamount > 0 && {
        discount: Yup.number()
          .min(0, "Discount cannot be negative")
          .nullable(),
      }),
      remark: Yup.string().when("payment_type", {
        is: (val) => val === "settlement",
        then: (schema) => schema.required("Remark is required for settlement"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values) => {
      await formik.setTouched({
        amount_to_be_pay: true,
        payment_type: true,
        discount: true,
        remark: true,
      });

      // Run validation manually
      const errors = await formik.validateForm();

      // If any errors exist — exit
      if (Object.keys(errors).length > 0) {
        return;
      }
      // Sanitize negative value to positive
      const amountToPay = Math.abs(values.amount_to_be_pay);
      try {
        const response = await apiService.put(`/customer_users/pay_now?extra_amount=${formik.values.extra_amount}&remark=${formik.values.remark}`, {
          id: customerId,
          payment_type: values.payment_type,
          amount_to_be_pay: amountToPay,
          discount: values.discount ? values.discount : 0,
        });
        onClose();
        toast.success(response.message, { duration: 2000 });
        fetchCustomers();
      } catch (errors) {
        handleError(errors);
      }
    },
  });


  const isAddAmount = dueamount <= 0;

  useEffect(() => {
    const baseDue = Number(dueamount) || 0;
    const extra = Number(formik.values.extra_amount) || 0;
    const totalAmount = baseDue + extra;
    formik.setFieldValue("totalAmount", totalAmount);
  }, [dueamount, formik.values.extra_amount]);

  useEffect(() => {
    const totalAmount = Number(formik.values.totalAmount) || 0;
    const amountToPay = Number(formik.values.amount_to_be_pay) || 0;
    const balanceDue = Math.max(totalAmount - amountToPay, 0);
    formik.setFieldValue("balanceDue", balanceDue);
  }, [formik.values.totalAmount, formik.values.amount_to_be_pay]);

  useEffect(() => {
    const totalAmount = Number(formik.values.totalAmount) || 0;
    const discount = Number(formik.values.discount) || 0;
    const updatedPayAmount = Math.max(totalAmount - discount, 0);
    formik.setFieldValue("amount_to_be_pay", updatedPayAmount);
  }, [formik.values.totalAmount, formik.values.discount]);

  useEffect(() => {
    if (formik.values.payment_type === "settlement") {
      setShowExtraAmount(true);
    }
  }, [formik.values.payment_type]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-20 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg p-6 w-[310px] s:w-[350px] md:w-[460px] relative">
        {/* Close Button */}
        <div
          className="absolute top-7 right-3 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px] flex justify-center items-center"
          onClick={onClose}
        >
          <icons.IoClose className="text-green700 text-xl" />
        </div>

        <h2 className="text-xl font-semibold mb-5 text-G500 text-start">
          {isAddAmount ? "Add Amount" : " Pay Now"} ({customerName})
        </h2>

        {/* Form */}
        <form
          onSubmit={formik.handleSubmit}
          className="space-y-4 text-start max-h-[70vh] overflow-y-auto vertical"
        >
          <div className="text-G300">
            <label className="block text-sm font-normal mb-2 text-start">
              Previous Due Amount
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">
                ₹
              </span>
              <input
                type="number"
                name="totalAmount"
                className="inputtext text-green placeholder:text-green"
                placeholder="0"
                value={formik.values.totalAmount}
                onChange={formik.handleChange}
                style={{ paddingLeft: "25px" }}
                disabled
              />
            </div>
          </div>

          {/* Discount */}
          {dueamount > 0 && (
            <>
              <div className="flex items-center justify-between mt-4">
                <label className="text-sm font-normal text-G500">Want to Add a Discount ?</label>
                <button
                  type="button"
                  className={`px-3 py-1 rounded-full text-sm border ${showDiscount ? 'bg-green text-white' : 'bg-white text-green border-green'}`}
                  onClick={() => setShowDiscount(!showDiscount)}
                >
                  {showDiscount ? 'Remove' : 'Add'}
                </button>
              </div>
              {showDiscount && (
                <div className="text-G300">
                  <label className="block text-sm font-normal mb-2 text-start">
                    Discount Amount (if any)
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
                    <input
                      type="number"
                      name="discount"
                      className="inputtext text-green placeholder:text-green"
                      placeholder="0"
                      style={{ paddingLeft: "25px" }}
                      value={formik.values.discount}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Prevent negative input
                        if (value.includes("-")) {
                          value = "0"; // Reset to 0 if user tries to enter "-"
                        }
                        let numericValue = value === "" ? "" : parseFloat(value) || 0;
                        const totalAmount = formik.values.totalAmount || 0;


                        if (numericValue > totalAmount) {
                          if (!isToastShown) {
                            setIsToastShown(true);
                            toast.error(
                              "Discount cannot be greater than the total amount",
                              { duration: 2000, onClose: () => setIsToastShown(false), });
                          }
                        } else {
                          setIsToastShown(false);
                          formik.setFieldValue("discount", numericValue);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "-") { e.preventDefault() }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === "") {
                          formik.setFieldValue("discount", 0);
                        }
                      }}
                      onWheel={(e) => e.target.blur()} // Prevent number scrolling by removing focus
                    />
                  </div>
                  {formik.touched.discount && formik.errors.discount && (
                    <div className="text-red text-sm text-start">{formik.errors.discount} </div>
                  )}
                </div>
              )}
            </>
          )}
          <div className="flex items-center justify-between mt-4">
            <label className="text-sm font-normal text-G500">Want to Add Extra Amount ?</label>
            <button
              type="button"
              className={`px-3 py-1 rounded-full text-sm border ${showExtraAmount ? 'bg-green text-white' : 'bg-white text-green border-green'}`}
              onClick={() => setShowExtraAmount(!showExtraAmount)}
            >
              {showExtraAmount ? 'Remove' : 'Add'}
            </button>
          </div>

          {(showExtraAmount || formik.values.payment_type === "settlement") && (
            <div className="flex gap-4 mt-2">
              <div className="text-G300">
                <label className="block text-sm font-normal mb-2 text-start">
                  Add extra amount
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
                  <input
                    type="number"
                    name="extra_amount"
                    className="inputtext text-green"
                    placeholder="0"
                    style={{ paddingLeft: "25px" }}
                    value={formik.values.extra_amount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </div>

              <div className="text-G300 ">
                <label className="block text-sm font-normal mb-2 text-start">
                  Remark {formik.values.payment_type === "settlement" && <span className="text-red">*</span>}
                </label>
                <input
                  type="text"
                  name="remark"
                  className="inputtext"
                  placeholder="remark"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.remark && formik.errors.remark && (
                  <div className="text-red text-sm">{formik.errors.remark}</div>
                )}
              </div>
            </div>
          )}
          {/* Amount to be Paid */}
          <div className="flex gap-4">
            <div className={`text-G300 ${isAddAmount ? "w-full" : ""}`} >
              <label className="block text-sm font-normal mb-2 text-start">
                Amount to be Paid
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">
                  ₹
                </span>
                <input
                  type="number"
                  name="amount_to_be_pay"
                  className="inputtext text-green placeholder:text-green"
                  placeholder="0"
                  value={formik.values.amount_to_be_pay === 0 ? "" : formik.values.amount_to_be_pay}
                  onChange={formik.handleChange}
                  onWheel={(e) => e.target.blur()}
                  onBlur={formik.handleBlur}
                  style={{ paddingLeft: "25px" }}
                />
              </div>
              {formik.touched.amount_to_be_pay &&
                formik.errors.amount_to_be_pay && (
                  <p className="text-red text-sm text-start">
                    {formik.errors.amount_to_be_pay}
                  </p>
                )}
            </div>
            {isAddAmount ? (
              <></>
            ) : (

              <div className="text-G300 ">
                <label className="block text-sm font-normal mb-2 text-start">
                  Balance Due
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
                  <input
                    type="text"
                    name="balanceDue"
                    className="inputtext text-green placeholder:text-green"
                    placeholder="0"
                    style={{ paddingLeft: "25px" }}
                    value={Math.trunc(formik.values.balanceDue)}
                    disabled
                  />
                </div>
              </div>
            )}
          </div>


          {/* Payment mode */}
          <div className="flex flex-col text-G300">
            <label className="block font-normal text-sm mb-2">
              Payment mode
            </label>
            <select
              name="payment_type"
              className="inputtext"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.payment_type}
            >
              <option value="">Select payment mode</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="upi">UPI</option>
              {userData.userType === "vendor" && (
                <option value="settlement">Settlement</option>
              )}
            </select>
            {formik.touched.payment_type && formik.errors.payment_type && (
              <div className="text-red text-sm">
                {formik.errors.payment_type}
              </div>
            )}
          </div>


          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={() => {
                formik.resetForm();
                onClose();
              }}
              className="w-[75px] sm:w-[93px] py-2 text-center text-sm bg-white text-green rounded-lg border border-green"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-[75px] sm:w-[93px] text-sm text-center py-2 rounded-lg bg-green text-white"
            >
              Confirm
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayNowPlayer;
